import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import {
    QualityGapAuditNotes,
    QualityGapNotes,
    QualityGapForm,
    QualityClear,
    HistoryRow,
} from '~/components/Patient';
import { ModalTabs } from '~/components/Patient/Gap/ModalTabs';
import { HealthHxItemType, gapIsUnderReview, healthHxTabs, useRoleActions } from '~/util';
import { ServicesContext, useMeasureAssociation } from '~/services';
import { BSModal } from '~/components/Bootstrap';
import { QualityGapReminders } from '~/components/Reminders';

interface QualityGapModalProps {
    qualityGap: GapType;
    patientId: number;
    handleClose: () => void;
    statusFilter?: number;
    defaultTab?: string;
    addReminderOpen?: boolean;
}
export function QualityGapModal({
    qualityGap,
    patientId,
    handleClose,
    statusFilter = 0,
    defaultTab,
    addReminderOpen = false,
}: QualityGapModalProps) {
    const context = React.useContext(ServicesContext);
    const rolePermission = useRoleActions('allow_save_edit_answer_quality_gap');
    const [loading, setLoading] = React.useState(false);
    const [formDirty, setFormDirty] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [disabled, setDisabled] = React.useState(false);
    const [tab, setTab] = React.useState('Measures');
    const [isRestarting, setIsRestarting] = React.useState(false);
    const measureAssociations = useMeasureAssociation(qualityGap.measure?.code);

    const tabs = ['Measures', 'Info', 'Reminders', 'Notes', 'History'];
    if (Array.isArray(measureAssociations) && measureAssociations.length > 0)
        tabs.splice(2, 0, 'Medications');
    if (qualityGap.measure?.instructions) tabs.splice(2, 0, 'Help');
    React.useEffect(() => {
        if (defaultTab) {
            setTab(defaultTab);
        }
    }, [defaultTab]);

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                await context.patient.getGap(qualityGap.id, qualityGap.type);
            } catch (error) {
                // Empty catch
            } finally {
                setLoading(false);
            }
        })();
    }, [context.patient, qualityGap.id, qualityGap.type]);

    const formId = `quality_gap_form_${qualityGap.id}`;

    const hasSave = !rolePermission || gapIsUnderReview(qualityGap) ? false : formDirty;

    return (
        <BSModal
            show
            onHide={handleClose}
            className="attestation-modal-card gap-modal"
            aria-labelledby={`pcm-qua-${qualityGap.id}`}
        >
            <Modal.Header closeButton>
                <Modal.Title id={`pcm-qua-${qualityGap.id}`} role="heading" aria-level={1}>
                    <div>
                        {qualityGap.measure?.quality_measure?.name ||
                            qualityGap.measure?.short_name}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ModalTabs tab={tab} tabs={tabs} setTab={setTab} />
                {loading && ['Info', 'Measures'].includes(tab) && (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                )}
                {tab === 'Info' && !loading && (
                    <div className="large-modal-scroll">
                        <p>
                            <strong>Measure Name</strong>
                            <br />
                            {qualityGap.measure?.quality_measure?.name}
                        </p>
                        <p>
                            <strong>Display Text</strong>
                            <br />
                            {qualityGap.description}
                        </p>
                        <p>
                            <strong>Measure Comment</strong>
                            <br />
                            {qualityGap.measure?.quality_measure?.comment}
                        </p>
                        <p>
                            <strong>Instructions</strong>
                            <br />
                            {qualityGap.measure?.quality_measure?.instructions}
                        </p>
                        {qualityGap.return_comment && (
                            <p>
                                <strong>Return Comment</strong>
                                <br />
                                {qualityGap.return_comment}
                            </p>
                        )}
                    </div>
                )}
                <div className={tab === 'Measures' && !loading ? 'large-modal-scroll' : 'd-none'}>
                    {!rolePermission && (
                        <div className="permissions-alert">
                            You don’t have permission to save responses on this record.
                        </div>
                    )}
                    <QualityGapForm
                        qualityGap={qualityGap}
                        formId={formId}
                        patientId={patientId}
                        setFormDirty={setFormDirty}
                        setSaving={setSaving}
                        onSubmitSuccess={handleClose}
                        statusFilter={statusFilter}
                        setDisabled={setDisabled}
                        isRestarting={isRestarting}
                    />
                </div>
                {tab === 'Help' && (
                    <div className="large-modal-scroll">
                        <div className="help-instructions-text">
                            {qualityGap.measure?.instructions}
                        </div>
                    </div>
                )}
                {tab === 'Medications' && (
                    <div className="note-scroll">
                        {measureAssociations?.map((row) => (
                            <HistoryRow
                                type={healthHxTabs.meds}
                                history={row as unknown as HealthHxItemType}
                                key={row.id}
                            />
                        ))}
                    </div>
                )}
                {tab === 'Reminders' && (
                    <QualityGapReminders
                        qualityGapId={qualityGap.id}
                        patientId={patientId}
                        addReminderOpen={addReminderOpen}
                    />
                )}
                {tab === 'Notes' && (
                    <QualityGapNotes entityId={qualityGap.id} patientId={patientId} />
                )}
                {tab === 'History' && (
                    <QualityGapAuditNotes entityId={qualityGap.id} gap={qualityGap} />
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" type="button" onClick={handleClose}>
                    {hasSave ? 'Cancel' : 'Close'}
                </button>
                <QualityClear
                    gap={qualityGap}
                    rolePermission={rolePermission}
                    tab={tab}
                    patientId={patientId}
                    setDisabled={setDisabled}
                    disabled={disabled}
                    onSuccess={handleClose}
                    isRestarting={isRestarting}
                    setIsRestarting={setIsRestarting}
                />
                {hasSave && (
                    <button
                        className="btn btn-primary"
                        type="submit"
                        form={formId}
                        disabled={disabled}
                    >
                        {saving && (
                            <Spinner animation="border" role="status" size="sm" className="me-1" />
                        )}
                        Save
                    </button>
                )}
            </Modal.Footer>
        </BSModal>
    );
}
